/**
 * Feature E02-F06-T03 (frontend, api-client + Query hook) — read the project
 * event timeline.
 *
 * Feature-owned (NOT in the shared `api/queries.ts`). Uses the shared `apiClient`
 * (auto-injects the session Bearer token, normalizes the response, never throws)
 * + `unwrap` (the single throw-at-the-Query-boundary helper). Contracts come from
 * `@eer/shared` so the SPA speaks the exact shape the Fastify route validates
 * before sending.
 *
 * Endpoint (E02-F06 route `routes/project-events.ts`):
 *   GET /projects/:id/events  → { success:true, events: ProjectEventDTO[] }
 *                               (newest-first, ≤50)
 *
 * `unwrapLegacySuccess` in the client strips the `success` flag, so the unwrapped
 * body is `{ events:[…] }` — validated here against the shared
 * `projectEventsResponseSchema` (sans the `success` literal) so a drifted shape
 * fails loudly at the boundary rather than rendering garbage.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { projectEventSchema, type ProjectEventDTO } from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';

/** The unwrapped body shape (`success` already stripped by the client). */
const eventsBodySchema = z.object({ events: z.array(projectEventSchema) });

/** Query key for a project's event timeline (feature-local; not in the shared qk). */
export function projectEventsKey(projectId: number): readonly unknown[] {
  return ['projects', 'events', projectId] as const;
}

/** Fetch one project's event timeline (GET /projects/:id/events). */
export async function fetchProjectEvents(projectId: number): Promise<ProjectEventDTO[]> {
  const result = await apiClient.get<unknown>(`/projects/${projectId}/events`);
  const body = unwrap(result);
  return eventsBodySchema.parse(body).events;
}

/**
 * Query hook for the project event timeline. Disabled until a positive
 * `projectId` is known (a project detail must be selected first). The data is the
 * newest-first ≤50 list the route returns, each row carrying a server-rendered RU
 * `description`.
 */
export function useProjectEvents(
  projectId: number | null | undefined,
): UseQueryResult<ProjectEventDTO[], ApiResultError> {
  const enabled = typeof projectId === 'number' && projectId > 0;
  return useQuery<ProjectEventDTO[], ApiResultError>({
    queryKey: projectEventsKey(enabled ? (projectId as number) : 0),
    queryFn: () => fetchProjectEvents(projectId as number),
    enabled,
    staleTime: 30_000,
  });
}
